import { useState, useCallback } from "react";
import { Unlock, Upload, Download, Trash2, FileText, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is unlocking password-protected PDFs free?",
    answer: "Yes, completely free with no signup. You need to know the password to unlock the PDF."
  },
  {
    question: "Can you unlock PDFs without a password?",
    answer: "No, you must know the correct password. We cannot crack or bypass PDF encryption."
  },
  {
    question: "What happens after unlocking?",
    answer: "The password protection is removed, creating a new PDF that anyone can open without a password."
  },
  {
    question: "Is my password transmitted securely?",
    answer: "Yes, your password is only used to decrypt the PDF and is never stored. Files auto-delete after 30 minutes."
  },
  {
    question: "Can I add password protection back?",
    answer: "Yes, use our Protect PDF tool to add new password protection to any PDF."
  }
];

const PdfUnlockPage = () => {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleUnlock = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (!password) {
      toast.error("Please enter the PDF password");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("password", password);

    try {
      const response = await axios.post(`${API}/pdf-unlock`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unlock failed - check your password");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "unlocked.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPassword("");
  };

  return (
    <>
      <SEOHead
        title="Unlock PDF Free Online | Remove PDF Password"
        description="Remove password protection from PDF files. Unlock PDF documents instantly. Free online PDF unlocker. No signup required."
        canonicalPath="/pdf-unlock"
        keywords="unlock PDF, remove PDF password, PDF unlocker free, decrypt PDF, open locked PDF"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <Unlock className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Unlock PDF Free Online
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Remove password protection from your PDF documents. Enter the password you know 
              and download an unlocked version. No signup required.
            </p>
          </div>

        {/* Upload & Options */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your protected PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Password Input */}
            <div className="border border-border p-6 space-y-4">
              <div>
                <Label>PDF Password *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter the PDF password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the password used to protect the PDF
                </p>
              </div>

              <div className="bg-secondary p-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> You must know the PDF password to unlock it. 
                  This tool cannot crack or bypass password protection.
                </p>
              </div>
            </div>

            <Button
              onClick={handleUnlock}
              disabled={!file || !password || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="unlock-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="w-5 h-5 mr-2" />
                  Unlock PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-green-500 flex items-center justify-center mx-auto mb-6">
                <Unlock className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Unlocked
              </h2>
              <p className="text-muted-foreground mb-6">{result.message}</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={handleReset} className="rounded-sm">
                  <Trash2 className="w-4 h-4 mr-2" /> Unlock Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-unlock" content={toolContent["pdf-unlock"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-unlock" />
      </div>
    </div>
    </>
  );
};

export default PdfUnlockPage;
